import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AmountInput, Button, DatePicker, Drawer, SearchSelect, StatusChip } from '../components'
import { useAccounts } from '../lib/api/accounts'
import { useCategories } from '../lib/api/categories'
import { useRecordInvoicePayment } from '../lib/api/invoices'
import { ApiError } from '../lib/api'
import type { Invoice } from '../lib/api/types'
import { fmtMoney } from '../lib/money'
import { outstandingToPost } from '../lib/invoice-status'

export function InvoicePaymentDrawer({
  invoice,
  counterpartyName,
  onClose,
}: {
  invoice: Invoice
  counterpartyName?: string
  onClose: () => void
}) {
  // Remainder still to post to cash (mirrors backend outstanding = total − Σ payments),
  // so a marked-paid-but-unposted invoice pre-fills the real amount instead of 0.
  const outstanding = outstandingToPost(invoice)

  const accounts = useAccounts({ organizationId: invoice.organizationId })
  const incomeCategories = useCategories({ kind: 'income' })
  const orgAccounts = useMemo(() => accounts.data ?? [], [accounts.data])

  const [amount, setAmount] = useState(outstanding.toString())
  const [accountId, setAccountId] = useState('')
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [categoryId, setCategoryId] = useState('')
  const [description, setDescription] = useState(
    invoice.payerName
      ? `Оплата рахунку ${invoice.number} — ${invoice.payerName}`
      : `Оплата рахунку ${invoice.number}`,
  )
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!accountId && orgAccounts[0]?.id) setAccountId(orgAccounts[0].id)
  }, [accountId, orgAccounts])

  useEffect(() => {
    if (!categoryId && incomeCategories.data && incomeCategories.data.length > 0) {
      setCategoryId(incomeCategories.data[0].id)
    }
  }, [categoryId, incomeCategories.data])

  const mutation = useRecordInvoicePayment()

  const submit = async () => {
    setError(null)
    try {
      await mutation.mutateAsync({
        invoiceId: invoice.id,
        amount: Number(amount),
        accountId,
        categoryId,
        date,
        description,
      })
      onClose()
    } catch (err) {
      setError(translatePayError(err))
    }
  }

  const account = orgAccounts.find((a) => a.id === accountId)
  const fxNote = account != null && invoice.currency !== 'UAH'

  return (
    <Drawer
      onClose={onClose}
      title={`Оплата рахунку ${invoice.number}${counterpartyName ? ` — ${counterpartyName}` : ''}`}
      width={520}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 16 }}>
        {orgAccounts.length === 0 && !accounts.isLoading && (
          <Warn>В організації немає рахунків. Створіть рахунок у Налаштуваннях → Рахунки.</Warn>
        )}
        {incomeCategories.data?.length === 0 && !incomeCategories.isLoading && (
          <Warn>
            Немає категорій типу «Надходження». Створіть категорію у Налаштуваннях → Категорії.
          </Warn>
        )}

        <Field label="Сума">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <AmountInput
              value={amount}
              onChange={setAmount}
              decimals={2}
              className="input num"
              style={{ flex: 1 }}
            />
            <StatusChip status={invoice.currency} />
          </div>
          <Hint>Залишок до сплати: {fmtMoney(outstanding, invoice.currency)}</Hint>
        </Field>

        <Field label="Рахунок зарахування">
          <SearchSelect
            className="input"
            value={accountId}
            onChange={setAccountId}
            placeholder="Оберіть рахунок…"
            options={orgAccounts.map((a) => ({ id: a.id, label: a.name, hint: a.currency }))}
          />
        </Field>

        <Field label="Дата">
          <DatePicker className="input" value={date} onChange={setDate} />
        </Field>

        <Field label="Категорія доходу">
          <SearchSelect
            className="input"
            value={categoryId}
            onChange={setCategoryId}
            placeholder="Оберіть категорію…"
            options={(incomeCategories.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
          />
        </Field>

        <Field label="Опис">
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="input"
          />
        </Field>

        {fxNote && (
          <Hint>
            Курс {invoice.currency}→UAH буде підставлено автоматично за датою. Якщо курсу немає,
            додайте його у Налаштуваннях → Курси.
          </Hint>
        )}

        {error && <div style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Скасувати
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={submit}
            disabled={mutation.isPending || !amount || !accountId || !categoryId}
          >
            {mutation.isPending
              ? 'Проводимо…'
              : `Провести ${fmtMoney(Number(amount) || 0, invoice.currency)}`}
          </Button>
        </div>
      </div>
    </Drawer>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span
        style={{
          fontSize: 11,
          color: 'var(--fg-3)',
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      {children}
    </label>
  )
}

function Hint({ children }: { children: ReactNode }) {
  return <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>{children}</span>
}

function Warn({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 6,
        background: 'var(--bg-3)',
        border: '1px solid var(--warning-fg)',
        fontSize: 13,
        color: 'var(--warning-fg)',
      }}
    >
      {children}
    </div>
  )
}

function translatePayError(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.body) as { error?: string }
      const code = parsed.error ?? ''
      const map: Record<string, string> = {
        missing_fx_rate: 'Курс на цю дату не знайдено. Додайте курс у Налаштуваннях → Курси.',
        amount_out_of_range: 'Сума більша за залишок до сплати.',
        account_mismatch: 'Рахунок належить іншій організації.',
        bad_category: 'Категорія має бути типу «надходження».',
        invoice_cancelled: 'Рахунок скасовано — оплату провести не можна.',
        invoice_not_found: 'Рахунок не знайдено.',
      }
      return map[code] ?? `Помилка: ${code || err.statusText || 'невідомо'}`
    } catch {
      return `Помилка: ${err.statusText}`
    }
  }
  return err instanceof Error ? err.message : 'Невідома помилка'
}
